/**
 * Top navigation bar.
 * Phase 15.5 + Phase 23.7 responsive: hamburger menu on mobile/tablet.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSystemMode } from '../../context/SystemModeContext';
import { fetchNeedsResponse } from '../../api/rfq';
import { fetchTodoMyCount } from '../../api/todos';
import { fetchPackingSlipNeedsReviewCount } from '../../api/purchasing';
import NotificationBell from '../agent/NotificationBell';
import MessageBadge from './MessageBadge';
import GlobalSearchBar from './GlobalSearchBar';

const NAV_LINKS = [
  { to: '/', label: 'Dashboard' },
  { to: '/quotes', label: 'Quotes' },
  { to: '/jobs/archived', label: 'Archived' },
  { to: '/manage', label: 'Manage' },
  { to: '/reports', label: 'Reports' },
  { to: '/documents', label: 'Documents' },
  { to: '/todos', label: 'To-Do', todoBadge: true },
  { to: '/email-review', label: 'RFQ Responses', badge: true },
  { to: '/receiving-review', label: 'Receiving Review', receivingBadge: true, adminOnly: true },
  { to: '/settings', label: 'Settings' },
];

export default function TopNav() {
  const { user, logout } = useAuth();
  const { isTestMode, display_name } = useSystemMode();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingReview, setPendingReview] = useState(0);
  const [todoCount, setTodoCount] = useState(0);
  const [receivingReviewCount, setReceivingReviewCount] = useState(0);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    async function fetchPending() {
      try {
        const result = await fetchNeedsResponse();
        if (!cancelled) setPendingReview(result.count || 0);
      } catch { /* silent */ }
    }
    async function fetchTodos() {
      try {
        const data = await fetchTodoMyCount();
        if (!cancelled) setTodoCount(data.count || 0);
      } catch { /* silent */ }
    }
    async function fetchReceivingReview() {
      if (!isAdmin) return;
      try {
        const count = await fetchPackingSlipNeedsReviewCount();
        if (!cancelled) setReceivingReviewCount(count || 0);
      } catch { /* silent */ }
    }
    fetchPending();
    fetchTodos();
    fetchReceivingReview();
    const interval = setInterval(() => {
      fetchPending();
      fetchTodos();
      fetchReceivingReview();
    }, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAdmin]);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <>
      {/* Test Mode Banner */}
      {isTestMode && (
        <div className="bg-blue-600 text-white text-center py-1.5 px-4 text-sm font-medium">
          <span className="inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            TEST MODE ACTIVE — Using test database. No changes affect live data.
          </span>
        </div>
      )}
      <nav className={isTestMode ? 'bg-blue-50 border-b border-blue-200' : 'bg-white border-b border-gray-200'}>
      <div className="px-4 h-14 flex items-center justify-between">
        {/* Left: Hamburger + Logo + nav links */}
        <div className="flex items-center gap-4">
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-1.5 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          <Link to="/" className={`text-lg font-bold whitespace-nowrap ${isTestMode ? 'text-blue-800' : 'text-foreground'}`}>
            {isTestMode ? 'JobLogic' : 'Pump Source'}
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              if (link.adminOnly && !isAdmin) return null;
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/'}
                  className={({ isActive }) =>
                    `relative px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`
                  }
                >
                  {link.label}
                  {link.badge && pendingReview > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {pendingReview}
                    </span>
                  )}
                  {link.todoBadge && todoCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {todoCount}
                    </span>
                  )}
                  {link.receivingBadge && receivingReviewCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {receivingReviewCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Right: Global Search, notification, user menu */}
        <div className="flex items-center gap-3">
          {/* Single unified search bar for POs, Jobs, etc. */}
          <div className="hidden sm:block">
            <GlobalSearchBar className="w-48 lg:w-64" />
          </div>

          <MessageBadge />
          <NotificationBell />

          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 rounded-md hover:bg-gray-50"
            >
              <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-medium">
                {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <span className="hidden lg:inline">
                {user?.first_name || user?.email || 'User'}
              </span>
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20 py-1">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className={`md:hidden border-t px-4 py-2 space-y-1 ${isTestMode ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'}`}>
          {NAV_LINKS.map((link) => {
            if (link.adminOnly && !isAdmin) return null;
            return (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm font-medium rounded-md ${
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                  }`
                }
              >
                {link.label}
              </NavLink>
            );
          })}
          {/* Mobile search */}
          <div className="sm:hidden pt-2">
            <GlobalSearchBar className="w-full" />
          </div>
        </div>
      )}
      </nav>
    </>
  );
}
